// Copyright (c) 2022 by Move Closer

import {
  ApiConnectorType,
  AppModule,
  AuthProvider,
  AuthServiceType,
  Container,
  EventbusType,
  IConfiguration,
  IConnector,
  IModal,
  Interfaces,
  ModalConnector,
  ModalType,
  Module
} from '@movecloser/front-core'

import {
  GraphQLConnector,
  GraphQlConnectorType,
  GraphQlDriver,
  GraphQLDriverType,
  IGraphQL,
  IGraphQLDriver,
  IRelatedService,
  ISiteService,
  RelatedService,
  RelatedServiceType,
  resolveDrivers,
  SiteServiceType
} from '../../contexts'
import { PRIVACY_CONFIG } from '../../support'

import { DrawerType, IDrawer } from './contracts/services'

import { graphqlConfig, graphqlConfigDebug, toasts } from './config'
import {
  IPrivacyManager,
  IPrivacyManagerType,
  IToastsService,
  PrivacyConfig,
  PrivacyManager,
  ToastsService,
  ToastsServiceType
} from './services'
import { DictionaryServiceType, IDictionaryService, Service } from './services/dictionary'
import { modalsRegistry } from './config/modals'
import { sharedQueries } from './config/queries'
import { ITabsService, TabItem, TabsService, TabsServiceType } from './services/tabs'
import { ISearchService, SEARCH_CONFIG, SearchService, SearchServiceType } from './services/search'
import { resources, trustedShopResources } from './config/resources'
import { IStoreConfigService, StoreConfigService, StoreConfigServiceType } from './services/store-config'
import { storeFactory as state } from './store'
import { ITrustedShop, TrustedShopRepositoryType } from './repositories/trustedShop'
import { TrustedShopRepository } from './repositories/trustedShop/repository'
import { INewsletterService, NewsletterService, NewsletterServiceType } from './services/newsletter'
import { ISelenaFormService, SelenaFormService, SelenaFormServiceType } from './services/selenaForms'
import { INipValidationService, NipValidationService, NipValidationServiceType } from './services/nip-validation'
import { ContactFormService, ContactFormServiceType, IContactFormService } from './services/contact-form'

/**
 * @author Katarzyna Otto <katarzyna.otto@movecloser.pl>
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl>
 */
@AppModule({
  name: 'shared',
  providers: (config: IConfiguration) => (bind: Interfaces.Bind) => {
    bind<IDictionaryService>(DictionaryServiceType).toDynamicValue((context) => {
      return new Service(
        context.container.get(GraphQlConnectorType)
      )
    }).inSingletonScope()
    bind<IDrawer>(DrawerType).toDynamicValue(() => {
      return new ModalConnector({})
    }).inSingletonScope()

    bind<IGraphQL>(GraphQlConnectorType).toDynamicValue((context) => {
      return new GraphQLConnector(
        config.byFile('queries'),
        context.container.get(GraphQLDriverType)
      )
    }).inSingletonScope()

    bind<IGraphQLDriver>(GraphQLDriverType).toDynamicValue(() => {
      return new GraphQlDriver(
        graphqlConfig,
        graphqlConfigDebug
      )
    }).inSingletonScope()

    if (typeof localStorage !== 'undefined') {
      bind<IPrivacyManager>(IPrivacyManagerType).toDynamicValue(() => {
        return new PrivacyManager(
          localStorage,
          config.byKey(
            PRIVACY_CONFIG,
            false,
            { enabled: false, options: [], required: [] }
          ) as PrivacyConfig
        )
      })
    }

    bind<INipValidationService>(NipValidationServiceType).toDynamicValue((context) => {
      return new NipValidationService(context.container.get(ApiConnectorType))
    })

    bind<IContactFormService>(ContactFormServiceType).toDynamicValue((context) => {
      return new ContactFormService(context.container.get(ApiConnectorType))
    })

    bind<IRelatedService>(RelatedServiceType).toDynamicValue(
      () => new RelatedService(resolveDrivers())
    )

    bind<IStoreConfigService>(StoreConfigServiceType).toDynamicValue(context => {
      return new StoreConfigService(context.container.get(GraphQlConnectorType))
    }).inSingletonScope()

    bind<ISearchService>(SearchServiceType).toDynamicValue((context) => {
      return new SearchService(
        context.container.get(ApiConnectorType),
        context.container.get(RelatedServiceType),
        context.container.get(SiteServiceType),
        config.byKey(SEARCH_CONFIG)
      )
    }).inSingletonScope()

    bind<ITrustedShop>(TrustedShopRepositoryType).toDynamicValue((context) => {
      return new TrustedShopRepository(
        context.container.get(ApiConnectorType)
      )
    })

    bind<ITabsService<TabItem>>(TabsServiceType).to(TabsService).inSingletonScope()
    bind<IToastsService>(ToastsServiceType).toDynamicValue(() => {
      return new ToastsService(toasts)
    }).inSingletonScope()

    bind<INewsletterService>(NewsletterServiceType).toDynamicValue(context => {
      return new NewsletterService(context.container.get(ApiConnectorType))
    }).inSingletonScope()

    bind<ISelenaFormService>(SelenaFormServiceType).toDynamicValue(context => {
      return new SelenaFormService(
        context.container.get(ApiConnectorType),
        context.container.get<ISiteService>(SiteServiceType),
        context.container.get<AuthProvider>(AuthServiceType)
      )
    }).inSingletonScope()
  },
  boot (container: Container) {
    const graph = container.get<IGraphQL>(GraphQlConnectorType)
    graph.useQueries(sharedQueries)

    const modalConnector = container.get<IModal>(ModalType)
    modalConnector.register(modalsRegistry)

    const connector = container.get<IConnector>(ApiConnectorType)
    connector.useResources(resources)
    connector.useResources(trustedShopResources)
  },
  state
})

export class SharedModule extends Module {}
